import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { YStack } from 'tamagui';
import { AppCard, AppSectionHero, AppTabBar, AppTabButton, AppTabShell, AppText } from '../../ui-foundation';

interface Tab {
  key: string;
  label: string;
  subtitle: string;
  placeholder?: string;
  content?: React.ReactNode;
}

interface TabbedPageShellProps {
  title: string;
  subtitle: string;
  tabs: Tab[];
  nudge?: React.ReactNode;
  guideRail?: React.ReactNode;
  hideGuideRailTabs?: string[];
  hideNudgeTabs?: string[];
  hero?: React.ReactNode;
}

export default function TabbedPageShell({
  title,
  subtitle,
  tabs,
  nudge,
  guideRail,
  hideGuideRailTabs = [],
  hideNudgeTabs = [],
  hero,
}: TabbedPageShellProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTab = searchParams.get('tab');
  const initialTab = tabs.find((tab) => tab.key === queryTab)?.key || tabs[0].key;
  const [activeTab, setActiveTab] = useState(initialTab);
  const current = tabs.find(t => t.key === activeTab)!;

  useEffect(() => {
    const nextTab = tabs.find((tab) => tab.key === queryTab)?.key || tabs[0].key;
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, queryTab, tabs]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', key);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <AppTabShell className="animate-fade-in">
      {hero || <AppSectionHero title={title} subtitle={subtitle} tone="compact" />}

      {hideNudgeTabs.includes(activeTab) ? null : nudge}

      {tabs.length > 1 ? (
        <YStack gap="$xs">
          <AppTabBar>
            {tabs.map(tab => (
              <AppTabButton
                key={tab.key}
                onPress={() => handleTabChange(tab.key)}
                active={activeTab === tab.key}
              >
                {tab.label}
              </AppTabButton>
            ))}
          </AppTabBar>

          {current.subtitle ? (
            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" paddingHorizontal="$xs">
              {current.subtitle}
            </AppText>
          ) : null}
        </YStack>
      ) : null}

      {current.content ? current.content : (
        <AppCard surface="secondary" alignItems="center" className="text-center">
          <AppText color="$colorMuted" fontSize="$sm" textAlign="center" maxWidth={320}>
            {current.placeholder || 'This area is being completed next. The tab is staying in place so the overall structure stays consistent.'}
          </AppText>
        </AppCard>
      )}

      {hideGuideRailTabs.includes(activeTab) ? null : guideRail}
    </AppTabShell>
  );
}
